import React, { useState, useEffect } from 'react';
import validationAuth from '../../../validationAuth';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle,
    Select, MenuItem, FormControl, InputLabel, useTheme, useMediaQuery, Card, CardContent,
    Chip, Stack, Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import * as common from '../../../commons/common';

const ScreenManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [categories, setCategories] = useState([]);
    const [editItem, setEditItem] = useState({});
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [newComDtlVal, setNewComDtlVal] = useState('');
    const [editCategoryComMstrId, setEditCategoryComMstrId] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchCategories = async () => {
            const url = `${common.getApiUrl()}/admin/com-mstr`;
            const token = localStorage.getItem('access_token');

            try {
                const response = await axios.get(url, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data) {
                    //점검상태 CM2, 민원항목관리 CM4은 시설관리 페이지에서 관리하는걸로 변경
                    const categoriesFilter = response.data
                        .filter(ctgr => ctgr.comMstrId !== 'CM2' && ctgr.comMstrId !== 'CM4')
                        .sort((a, b) => a.comMstrId.localeCompare(b.comMstrId));
                    setCategories(categoriesFilter);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };

        fetchCategories();
    }, []);

    const handleAddItem = (category) => {
        setNewComDtlVal(String(category.comCdDtls.length)); //comDtlVal 보내야하니까 일단 length로 추가값 해놓기
        setEditItem({ comDtlNm: '', comDtlVal: '', comMstrId: category.comMstrId });
        setEditCategoryComMstrId(category.comMstrId);
        setEditDialogOpen(true);
    };

    const handleRemoveItem = async (id, category) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            const token = localStorage.getItem('access_token');
            try {
                await axios.delete(`${common.getApiUrl()}/admin/com-dtl/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                setCategories(categories.map(cat => cat.comMstrId === category
                    ? { ...cat, comCdDtls: cat.comCdDtls.filter(item => item.comDtlId !== id) }
                    : cat
                ));
            } catch (error) {
                console.error('Error deleting item:', error);
            }
        }
    };

    const handleEditItem = (item, category) => {
        setEditItem(item);
        setEditCategoryComMstrId(category);
        setEditDialogOpen(true);
    };

    const handleEditDialogClose = () => {
        setEditDialogOpen(false);
        setEditItem(null);
        setError('');
    };

    const handleEditDialogSave = async () => {
        const token = localStorage.getItem('access_token');

        try {
            let itemToSave = { ...editItem };

            // CM7은 이미 Y/N 값으로 설정되어 있으므로 변환 필요 없음

            if (editItem.comDtlId) {
                await axios.put(`${common.getApiUrl()}/admin/com-dtl/${editItem.comDtlId}`, itemToSave, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } else {
                // 추가이면
                const addEditItem = {
                    ...itemToSave,
                    comMstrId: String(editCategoryComMstrId),
                    comDtlVal: String(newComDtlVal)
                };

                if (addEditItem)
                    await axios.post(`${common.getApiUrl()}/admin/com-dtl`, addEditItem, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
            }

            // Refresh the category data
            const url = `${common.getApiUrl()}/admin/com-mstr`;
            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                const sortedCategories = response.data
                    .filter(ctgr => ctgr.comMstrId !== 'CM2' && ctgr.comMstrId !== 'CM4')
                    .sort((a, b) => a.comMstrId.localeCompare(b.comMstrId));
                setCategories(sortedCategories);
            }
        } catch (error) {
            console.error('Error saving item:', error);
        }
        handleEditDialogClose();
    };

    const handleEditChange = (field, value) => {
        setEditItem({ ...editItem, [field]: value });
    };

    // 모바일 카드 뷰 렌더링
    const renderMobileCard = (category) => (
        <Card
            key={category.comMstrId}
            elevation={0}
            sx={{
                mb: 3,
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '12px',
                overflow: 'hidden',
            }}
        >
            <CardContent sx={{ p: 0 }}>
                {/* 카테고리 헤더 */}
                <Box sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: '600',
                            color: '#333',
                            fontSize: '16px',
                        }}
                    >
                        {category.comMstrNm}
                    </Typography>
                    {category.isAdditional === 'Y' && (
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<AddIcon sx={{ fontSize: '16px' }} />}
                            onClick={() => handleAddItem(category)}
                            sx={{
                                borderRadius: '8px',
                                fontWeight: '600',
                                fontSize: '12px',
                                py: 0.75,
                                px: 1.5,
                                textTransform: 'none',
                            }}
                        >
                            추가
                        </Button>
                    )}
                </Box>

                {/* 아이템 리스트 */}
                <Box sx={{ p: 1 }}>
                    {category.comCdDtls && category.comCdDtls.map((item, index) => (
                        <Box key={item.comDtlId}>
                            <Box sx={{
                                p: 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: '#333',
                                            mb: 0.5,
                                        }}
                                    >
                                        {category.comMstrId === 'CM5' ? '구분 (m)' : '구분'}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: '15px',
                                            color: '#666',
                                            fontWeight: '400',
                                        }}
                                    >
                                        {category.comMstrId === 'CM7'
                                            ? (item.comDtlNm === 'Y' ? '예' : '아니오')
                                            : item.comDtlNm}
                                    </Typography>
                                </Box>

                                {/* 버튼들을 가로로 배치 */}
                                <Stack direction="row" spacing={0.5}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        startIcon={<EditIcon sx={{ fontSize: '14px' }} />}
                                        onClick={() => handleEditItem(item, category.comMstrId)}
                                        sx={{
                                            borderRadius: '6px',
                                            fontWeight: '500',
                                            fontSize: '11px',
                                            py: 0.5,
                                            px: 1,
                                            minWidth: 'auto',
                                            textTransform: 'none',
                                        }}
                                    >
                                        수정
                                    </Button>
                                    {category.comMstrId !== "CM5" && item.comDtlVal !== "0" && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            startIcon={<DeleteIcon sx={{ fontSize: '14px' }} />}
                                            onClick={() => handleRemoveItem(item.comDtlId, category.comMstrId)}
                                            sx={{
                                                borderRadius: '6px',
                                                fontWeight: '500',
                                                fontSize: '11px',
                                                py: 0.5,
                                                px: 1,
                                                minWidth: 'auto',
                                                textTransform: 'none',
                                            }}
                                        >
                                            삭제
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                            {index < category.comCdDtls.length - 1 && (
                                <Divider sx={{ mx: 2 }} />
                            )}
                        </Box>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );

    // 기존 PC 테이블 뷰 렌더링 (원본 그대로 유지)
    const renderDesktopTable = (category) => {
        const commonCellStyle = {
            padding: '8px 16px',
            textAlign: 'left',
            fontWeight: 'bold',
            borderRight: '1px solid #ddd'
        };

        const commonHeadCellStyle = {
            borderBottom: '2px solid #ddd',
            borderRight: '1px solid #ddd'
        };

        const commonBodyCellStyle = {
            borderBottom: '1px solid #ddd',
            borderRight: '1px solid #ddd'
        };

        const fixedCellWidths = {
            firstCell: '100px',
            secondCell: '150px',
            thirdCell: '150px',
            fourthCell: '100px'
        };

        const buttonStyle = {
            minWidth: '80px',
            margin: '0 4px'
        };

        return (
            <Box sx={{ marginBottom: '20px' }} key={category.comMstrId}>
                <Typography variant="h7" sx={{ fontWeight: 'bold' }}>{category.comMstrNm}</Typography>
                <TableContainer component={Paper} sx={{ marginTop: '10px', marginBottom: '50px' }}>
                    <Table sx={{ borderCollapse: 'collapse' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{
                                    ...commonCellStyle, ...commonHeadCellStyle,
                                    width: fixedCellWidths.firstCell
                                }}></TableCell>
                                {category.comMstrId == "CM5" ? (
                                    <TableCell style={{
                                        ...commonCellStyle, ...commonHeadCellStyle,
                                        width: fixedCellWidths.secondCell
                                    }}>구분 (m)</TableCell>
                                ) : (<TableCell style={{
                                    ...commonCellStyle, ...commonHeadCellStyle,
                                    width: fixedCellWidths.secondCell
                                }}>구분</TableCell>)}
                                <TableCell
                                    style={{
                                        ...commonCellStyle, ...commonHeadCellStyle,
                                        width: fixedCellWidths.fourthCell
                                    }}>
                                    {category.isAdditional === 'Y' && (  //isAdditional 값이 N 일 경우 추가 버튼 안나오게
                                        <Button variant="contained" color="primary"
                                                onClick={() => handleAddItem(category)} sx={buttonStyle}>
                                            추가
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ backgroundColor: '#f5f5f5' }}>
                            {category.comCdDtls && category.comCdDtls.map((item) => (
                                <TableRow key={item.comDtlId}>
                                    <TableCell style={{
                                        ...commonCellStyle, ...commonBodyCellStyle,
                                        width: fixedCellWidths.firstCell
                                    }}>{category.comMstrNm}</TableCell>
                                    <TableCell style={{
                                        ...commonCellStyle, ...commonBodyCellStyle,
                                        width: fixedCellWidths.secondCell
                                    }}>
                                        {category.comMstrId === 'CM7'
                                            ? (item.comDtlNm === 'Y' ? '예' : '아니오')
                                            : item.comDtlNm}
                                    </TableCell>
                                    <TableCell style={{
                                        ...commonCellStyle, ...commonBodyCellStyle,
                                        width: fixedCellWidths.fourthCell
                                    }}>
                                        <Button variant="outlined" color="primary"
                                                onClick={() => handleEditItem(item, category.comMstrId)}
                                                sx={buttonStyle}>
                                            수정
                                        </Button>
                                        {category.comMstrId !== "CM5" && item.comDtlVal !== "0" && (
                                            <Button variant="outlined"
                                                    color="error"
                                                    sx={{ ...buttonStyle }}
                                                    onClick={() => handleRemoveItem(item.comDtlId, category.comMstrId)}>
                                                삭제
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            p: isMobile ? 1 : 0,
        }}>
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 'bold',
                    mb: isMobile ? 2 : 3,
                }}
            >
                화면 설정
            </Typography>

            <Box sx={{ marginTop: isMobile ? 0 : '30px' }}>
                {categories.map(category =>
                    isMobile ? renderMobileCard(category) : renderDesktopTable(category)
                )}
            </Box>

            <Dialog
                open={editDialogOpen}
                onClose={handleEditDialogClose}
                PaperProps={{
                    style: {
                        minWidth: '400px',
                        ...(isMobile && {
                            margin: '16px',
                            width: 'calc(100% - 32px)',
                            maxWidth: 'none',
                            borderRadius: '12px',
                        })
                    }
                }}
            >
                <DialogTitle sx={{
                    ...(isMobile && {
                        fontWeight: '600',
                        fontSize: '18px',
                        pb: 1,
                        borderBottom: '1px solid rgba(0,0,0,0.08)'
                    })
                }}>
                    {editItem?.comDtlId ? '항목 수정' : '항목 추가'}
                </DialogTitle>
                <DialogContent style={{ paddingTop: '20px' }}>
                    {editCategoryComMstrId === 'CM7' ? (
                        <FormControl fullWidth margin="dense">
                            <InputLabel id="yes-no-select-label">구분</InputLabel>
                            <Select
                                labelId="yes-no-select-label"
                                value={editItem?.comDtlNm === 'Y' ? 'Y' : 'N'}
                                label="구분"
                                onChange={(e) => handleEditChange('comDtlNm', e.target.value)}
                                sx={isMobile ? { borderRadius: '8px' } : {}}
                            >
                                <MenuItem value="Y">예</MenuItem>
                                <MenuItem value="N">아니오</MenuItem>
                            </Select>
                        </FormControl>
                    ) : (
                        <TextField
                            margin="dense"
                            label="구분"
                            fullWidth
                            autoComplete="off"
                            value={editItem?.comDtlNm || ''}
                            onChange={(e) => handleEditChange('comDtlNm', e.target.value)}
                            sx={isMobile ? {
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                }
                            } : {}}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={isMobile ? {
                    p: 3,
                    pt: 2,
                    borderTop: '1px solid rgba(0,0,0,0.08)',
                    gap: 1
                } : {}}>
                    <Button
                        onClick={handleEditDialogClose}
                        sx={isMobile ? {
                            color: 'primary',
                            borderRadius: '8px',
                            fontWeight: '500',
                            textTransform: 'none',
                            flex: 1
                        } : { color: 'red' }}
                    >
                        취소
                    </Button>
                    <Button
                        onClick={handleEditDialogSave}
                        color="primary"
                        variant={isMobile ? "contained" : "text"}
                        sx={isMobile ? {
                            borderRadius: '8px',
                            fontWeight: '600',
                            textTransform: 'none',
                            flex: 1
                        } : {}}
                    >
                        저장
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default validationAuth(ScreenManagement);